import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import Konva from 'konva';
import { Stage, Layer, Rect, Group, Line, Text } from 'react-konva';
import { Howl } from 'howler';
import { getSoundPropsById, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useKeyEvents } from '../../lib/hooks.js';

import Spinner from '../builder/Spinner';
import { useImageLoader } from '../../lib/hooks.js';


const item_types = ['milestone'];

const standard_board_size = 600;	// px

const StackitCanvas = forwardRef((props, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ indexed_items, setIndexedItems ] = useState({});
	const [ reset, setReset ] = useState({ reset:true });
	const [ full_loaded, setFullLoaded ] = useState(false);
	const timer = useRef(null);

	const stage = props?.stage ?? {};
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const is_playable = props?.is_playable ?? ((!is_builder || is_test) ? true : false);

	const difficulty = stage?.stack_level ?? 'normal';
	const max_num_rows = parseInt(stage?.stack_num_rows ?? 30) + 1;
	const initial_num_blocks = Math.min(parseInt(stage?.stack_initial_row ?? 6), (max_num_rows - 4));
	const milestones_color = stage?.stack_milestones_color ?? 'rgba(0, 0, 0, 0.4)';
	const display_outter_border = stage?.display_outter_border ?? true;
	//const initial_num_blocks = Math.min(30, Math.ceil(max_num_rows / 2) + 2);
	
	const display_milestone_lines = stage?.stack_display_milestones ?? false;
	const display_grid = stage?.stack_display_grid ?? false;
	
	const corner_radius = parseInt(stage?.stack_block_corner_radius ?? 0);

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const game_score = useRef(0);
	const points_per_level = 100;
	const points_per_cell = 1;

	const keyPress = () => {
		if(!is_playable) return;
		stopLevel();
	};

	useKeyEvents({
		32:keyPress,
	}, 'keydown', [ indexed_items ]);

	const size = useRef(standard_board_size);

	const calculateItemSize = () => {
		return size.current / max_num_rows;
	};

	const item_size = useRef(calculateItemSize());
	const refs = useRef([]);
	const game_ended = useRef(false);
	const stoppedGame = useRef(false);
	const animations = useRef([]);
	const level = useRef(0);

	const getLevelAtPercentage = (percent) => {
		return Math.ceil(size.current * percent / item_size.current);
	};

	/* SOUNDS - ini */
	const durations = {
		success:2.0,
		applause:7.0,
		funny_laugh:1.5,
		evil_laugh:2.5,
		fail:4,
		oh:1.5,
	};

	const sound_durations = useRef({
		success:((stage?.game_soundfx_ok ?? 'applause') != 'none') ? durations[stage?.game_soundfx_ok ?? 'applause'] : 2,
		fail_time:((stage?.game_soundfx_ko_time ?? 'evil_laugh') != 'none') ? durations[stage?.game_soundfx_ko_time ?? 'evil_laugh'] : 2,
	});

	const stack_row = ((stage?.stack_row_soundfx ?? 'pop') != 'none') ? new Howl({ ...getSoundPropsById(stage?.stack_row_soundfx ?? 'pop') }) : false;
	const stackRow = () => {
		if(stack_row === false) return;
		stack_row.play();
	};

	const sound_success_game = ((stage?.game_soundfx_ok ?? 'applause') != 'none') ? new Howl({ ...getSoundPropsById(stage?.game_soundfx_ok ?? 'applause') }) : false;
	const soundSuccessGame = () => {
		if(sound_success_game === false) return;
		sound_success_game.play();
	}

	const sound_time_up_game = ((stage?.game_soundfx_ko_time ?? 'evil_laugh') != 'none') ? new Howl({ ...getSoundPropsById(stage?.game_soundfx_ko_time ?? 'evil_laugh') }) : false;
	const soundTimeUpGame = () => {
		if(sound_time_up_game === false) return;
		sound_time_up_game.play();
	}
	/* SOUNDS - end */

	const _milestones = stage?.stack_milestones ?? [{
		id:'level1',
		title:`${i18n("Level")} 1`,
		min_level:0,
		multiplier:1,
		color:props?.main_color ?? 'rgba(30, 34, 170, 1)',
		final_color:props?.main_color ?? 'rgba(30, 34, 170, 1)',
		final_image:null,
		full_final_image:null,
		image:null,
	}];

	const milestones = [];
	(_milestones.sort((a, b) => {
		return (parseInt(a.min_level) < parseInt(b.min_level)) ? -1 : 1;
	})).map((ms) => {
		const milestone_sound = ((ms?.sound_fx ?? 'evil_laugh') != 'none') ? new Howl({ ...getSoundPropsById(ms?.sound_fx ?? 'evil_laugh') }) : false;

		sound_durations.current[`ms_${ms.id}`] = (((ms?.sound_fx ?? 'evil_laugh') != 'none') ? durations[ms?.sound_fx ?? 'evil_laugh'] : 2);
	
		milestones.push({
			id:ms.id,
			title:ms?.title ?? i18n("Level"),
			min_level:getLevelAtPercentage(parseInt(ms?.min_level ?? 10) / 100),
			multiplier:parseInt(ms?.multiplier ?? 1),
			feedback:ms?.feedback,
			color:ms.color,
			final_color:ms.final_color,
			final_image:ms.final_image,
			full_final_image:ms.full_final_image,
			image:ms?.image,
			milestoneSound:() => {
				if(milestone_sound === false) return;
				milestone_sound.play();
			},
		});
	});

	// string of values to execute updateMilestonesLayout() if changed any of them:
	const getMilestoneStringify = (ms) => {
		return `${ms.id}_${ms.color}_${ms.image}`;
	};

	const getMilestoneAtLevel = (level) => {
		let actual_milestone = 0;
		milestones.map((ms, m) => {
			if(level > ms.min_level) {
				actual_milestone = m;
			}
		});
		return milestones[actual_milestone];
	};

	const isAnimatable = (item) => {
		return (item == undefined || item.parent == undefined) ? false : true;
	};

	const updateMilestonesLayout = () => {
		for(let i = 0; i < max_num_rows; i++) {
			const l = getTopLevel(i);

			if(!isAnimatable(refs.current[l])) return;

			const new_milestone = getMilestoneAtLevel(i);
			const has_milestone_changed = (refs.current[l].attrs.milestone_id != getMilestoneStringify(new_milestone)) ? true : false;

			if(has_milestone_changed) {
				refs.current[l].setAttrs({
					milestone_id:new_milestone.id,
				});

				let item_img = {
					fill:new_milestone.color,
				};
				if(isNotEmpty(new_milestone, 'image')) {
					const img = (indexed_items?.milestone ?? []).find((m) => (m.name == new_milestone.image));
	
					if(img) {
						item_img = {
							fillPatternImage:img.fillPatternImage,
							fillPatternRepeat:'no-repeat',
							fillPatternScaleX:img.scale,
							fillPatternScaleY:img.scale,
						};
					}
					item_img.fillPriority = 'pattern';
				} else {
					item_img.fillPriority = 'color';
				}

				refs.current[l].getChildren((col_item) => {
					if(col_item.attrs?.is_ghost ?? false) {
						col_item.setAttrs({
							fill:new_milestone.color,
						});
						return;
					}

					col_item.setAttrs(item_img);
				});

				refs.current['layer'].draw();
			}
		}
	};

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			exitGame();
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	useEffect(() => {
		if(!full_loaded || !is_builder) return;

		updateMilestonesLayout();
	}, [ stage.stack_milestones ]);

	useEffect(() => {
		if(!full_loaded) return;

		if(refs.current['layer'] != undefined) {
			refs.current['layer'].draw();

			setTimeout(() => {
				startAnimations();
			}, 0);
		}

		return () => {
			clearTimeout(timer.current);
		}
	}, [ full_loaded ]);

	useEffect(() => {
		resetGame();
	}, [ 
		stage.stack_num_rows,
		stage.stack_initial_row,
		stage.stack_block_corner_radius,
	]);

	useEffect(() => {
		item_size.current = calculateItemSize();
		setIndexedItems({ ...indexed_items });
	}, [
		stage.stack_num_rows,
	]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			stoppedGame.current = true;

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const cleanup = () => {
				exitGame();

				if(cb != undefined) {
					setTimeout(() => {
						cb();
					}, 0);
				}
			}

			if(success_game === false) {
				soundTimeUpGame();
			}

			cleanup();
		},

		stopBar() {
			stopLevel();
		},
	}), [ indexed_items ]);

	const getNumBlocksInLevel = (level) => {
		return Math.max(0, refs.current[(level != undefined) ? level : getTopLevel()].attrs.num_blocks);
	};

	const getLevelDuration = () => {
		const max_time = 3;	// in sec
		if(difficulty == 'easy') {
			return Math.max(2, max_time - 0.07 * level.current);
		}

		const count_max_rows = max_num_rows + Math.ceil(max_num_rows / 8);

		let anim_duration = max_time - 2.5 * level.current / count_max_rows;

		if(difficulty == 'hard') {
			anim_duration = Math.max(1.2, anim_duration - 1.2 * Math.random());
		}

		return Math.round(10 * anim_duration) / 10;
	};

	const slideTo = (direction = 1) => {
		const top_level = getTopLevel();
		if(refs.current[top_level] == undefined) return;

		const width = getNumBlocksInLevel(top_level) * item_size.current;

		animations.current[level.current] = new Konva.Tween({
			node:refs.current[top_level],
			x:(direction > 0) ? size.current : -width,
			duration:getLevelDuration(),
			onFinish:() => {
				if(refs.current[top_level] == undefined) {
					animations.current[level.current].pause();
					animations.current[level.current] = null;
					return;
				}

				const new_direction = -1 * direction;
				refs.current[top_level].attrs.direction = new_direction;
				slideTo(new_direction);
			}
		}).play();
	};
 
	const exitGame = () => {
		if(refs.current[`main_stage`]) {
			killAllAnimations();

			refs.current[`main_stage`].removeChildren();
			refs.current[`main_stage`].destroy();
		}

		//setFullLoaded(false);
	};

	const prepareImagesToLoad = () => {
		const images = [];
 
		if(isNotEmpty(stage, 'bg_image')) {
			images.push({
				id:'full_bg',
				name:'full_bg',
				url:stage.full_bg_image,
				use_full_size:true,
			});
		} 

		(stage?.stack_milestones ?? []).map((image) => {
			if(!isNotEmpty(image, 'image')) return;

			images.push({
				id:image.type,
				name:image.image,
				url:image.full_image,
				image:image.full_image,
				fit:'contain',
			});
		});

		return images;
	};

	// Done this to change the loaded images only when images in milestones are changed
	// (the useMemo will not be fired if any other attribute):
	const memo_key = (stage?.stack_milestones ?? []).reduce((a, c) => {
		return a + c.image;
	}, '');

	const images = useMemo(prepareImagesToLoad, [
		stage.full_bg_image,
		memo_key,
	]);

	useImageLoader(images, (loaded_images) => {
		const current = {};
		setFullLoaded(false);

		Object.keys(loaded_images).map((image_name) => {
			const image = loaded_images[image_name];

			if(!isNotEmpty(image, 'img')) return;

			const type = image.id;
			const new_image = adaptImageSizes({
				fillPatternImage:image.img,
				fit:image.fit,
				image_w:image.w,
				image_h:image.h,
				use_full_size:image?.use_full_size ?? false,
			});

			if(item_types.includes(type)) {
				if(current[type] == undefined) current[type] = [];
				current[type] = [...current[type], { ...new_image, name:image_name }];
				return;
			}

			current[type] = new_image;
		});

		setIndexedItems(current);

		setTimeout(() => {
			gameInit();
		}, 0);
	});

	const calcImageSize = (w, h, type = 'cover', use_full_size = false) => {
		const w_prop = (((!use_full_size) ? item_size.current : size.current) + 1) / w;
		const h_prop = (((!use_full_size) ? item_size.current : size.current) + 1) / h;

		if(type == 'contain') return Math.min(w_prop, h_prop);

		return Math.max(w_prop, h_prop);
	};

	const calcItemOffset = (item, _size) => {
		let offset_x = (_size * item.w - item_size.current) / 2;
		let offset_y = (_size * item.h - item_size.current) / 2;

		return { x:offset_x, y:offset_y };
	};

	const calculateScoreAtLevel = (level) => {
		const total_items_in_level = getNumBlocksInLevel(getTopLevel(level));
		const actual_milestone = getMilestoneAtLevel(level);

		return total_items_in_level * parseInt(actual_milestone.multiplier) * points_per_cell;
	};

	const adaptImageSizes = (image) => {
		const _scale = calcImageSize(image.image_w, image.image_h, image.fit, image.use_full_size);
		const _offset = calcItemOffset({ w:image.image_w, h:image.image_h }, _scale);
	
		return { ...image, scale:_scale, offset:_offset };
	};

	useEffect(() => {
		Object.keys(indexed_items).map((item) => {
			if(Array.isArray(indexed_items[item])) {
				const new_items = [];
				indexed_items[item].map((it) => {
					new_items.push(adaptImageSizes(it));
				});
				indexed_items[item] = new_items;
			} else {
				indexed_items[item] = adaptImageSizes(indexed_items[item]);
			}
		});

		setIndexedItems({ ...indexed_items });

		if(refs.current['layer'] != undefined) refs.current['layer'].draw();
	}, [ item_size.current ]);

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				item_size.current = calculateItemSize();
				props.startTimer();

				setFullLoaded(true);
			});
		} else {
			setFullLoaded(true);
		}
	};

	const startAnimations = () => {
		game_ended.current = false;

		generateRow(0);
		
		if(is_playable) {
			level.current = 1;
			generateRow(level.current);
		} else {
			let actual_rows = initial_num_blocks;

			for(let i = 1; i < max_num_rows; i++) {
				actual_rows -= Math.round(Math.random());
				actual_rows = Math.max(3, actual_rows);

				level.current = i;
				generateRow(i, actual_rows);
			}
		}
	};

	const getTopLevel = (custom_level) => {
		return `slider_${(custom_level == undefined) ? level.current : custom_level}`;
	};

	const checkIfMounted = () => {
		// game is unmounted
		if(refs.current[`main_stage`] == undefined) return false;
		return true;
	};

	const updateScore = (new_score) => {
		// calculateScoreAtLevel(level)

		game_score.current = game_score.current + new_score;
		if(props.onScore != undefined) props.onScore(game_score.current);
	};

	const fallAnimation = (level, col, direction = 1) => {
		if(refs.current[`${level}_${col}`] == undefined) return;

		refs.current[`${level}_${col}`].moveToTop();

		if(!isAnimatable(refs.current[`${level}_${col}`])) return;

		refs.current[`${level}_${col}`].to({
			scaleX:0.8,
			scaleY:0.8,
			strokeWidth:2,
			stroke:'rgba(0, 0, 0, 0.4)',
			y:-0.2 * item_size.current,
			rotation:-direction * (5 + Math.random() * 8),
			duration:0.15,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:(() => {
				if(!isAnimatable(refs.current[`${level}_${col}`])) return;

				refs.current[`${level}_${col}`].to({
					scaleX:0.1,
					scaleY:0.1,
					y:size.current + 2 * item_size.current,
					rotation:direction * (45 + Math.random() * 45),
					duration:0.3 + level * 0.05,
					easing:Konva.Easings.StrongEaseIn,
					onFinish:() => {
						refs.current[`${level}_${col}`].destroy();
					},
				});
			}),
		});
	};

	const animateAllCells = (singleAnimation, is_success = true, delay_between_items = 15) => {
		let accumulated = 0;
		let i = 0;
		let total_num_items = 0;
		let is_last_item = false;

		const final_level = (is_success) ? max_num_rows : (level.current - 1);

		[...Array(refs.current['levels'].getChildren().length).keys()].map((item_l, level) => {
			if(level == 0) return;

			const level_ref = getTopLevel(level);
			total_num_items += refs.current[level_ref].getChildren().length - 1;

			refs.current[level_ref].getChildren((col_item) => {
				if(col_item.attrs?.is_ghost ?? false) return;

				if(!isAnimatable(col_item)) return;

				accumulated++;
				setTimeout(() => {
					i++;

					if(!isAnimatable(col_item)) return;

					col_item.moveToTop();

					if(level == final_level - 1) {
						if(!is_last_item && accumulated == total_num_items) {
							is_last_item = true;

							col_item.setAttrs({
								is_last_item:true,
								item_count:accumulated,
							});
						}
					}

					singleAnimation(col_item);
				}, ((accumulated - 1) * delay_between_items));
			})
		});
	};

	const animateSuccess = (cb) => {
		const single_animation_duration = 0.8;
		const interval_between_cell_animations = 15; // in ms

		animateAllCells((col_item) => {
			updateScore(points_per_cell);

			if(!isAnimatable(col_item)) return;
			col_item.to({
				scaleX:0.8,
				scaleY:0.8,
				y:item_size.current / 2 - item_size.current / 4,
				strokeWidth:2,
				stroke:'rgba(0, 0, 0, 0.4)',
				duration:single_animation_duration / 2,
				easing:Konva.Easings.StrongEaseOut,
				onFinish:() => {
					if(!isAnimatable(col_item)) return;
	
					col_item.to({
						scaleX:1,
						scaleY:1,
						y:item_size.current / 2,
						strokeWidth:0,
						stroke:'rgba(0, 0, 0, 0)',
						duration:single_animation_duration / 2,
						easing:Konva.Easings.StrongEaseIn,
					});
				},
			});
	
			if(!isAnimatable(col_item)) return;
			col_item.to({
				rotation:360,
				duration:single_animation_duration,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					if(cb != undefined && (col_item.attrs?.is_last_item ?? false)) {
						const wait_until_cb = sound_durations.current.success * 1000 - col_item.attrs?.item_count * interval_between_cell_animations;

						setTimeout(() => {
							cb();
						}, Math.max(500, wait_until_cb));
					}
				},
			});
		}, true, interval_between_cell_animations)
	};

	const animateFail = (milestone_id, cb) => {
		const single_animation_duration = 1;
		const interval_between_cell_animations = 22; // in ms
		const end_scale = 0.7;
		const end_rotation_delta = 40;

		if(level.current == 2) {
			// we could not stack any single item (bruh)
			setTimeout(() => {
				cb();
			}, sound_durations.current[`ms_${milestone_id}`] * 1000);
			return;
		}

		animateAllCells((col_item) => {
			if(!isAnimatable(col_item)) return;
			col_item.to({
				y:(col_item.attrs.level) * (item_size.current / 2 + 0.3 * end_scale * item_size.current),
				x:col_item.x() + item_size.current / 2 * Math.random() - item_size.current / 2,
				scaleX:end_scale,
				scaleY:end_scale,
				rotation:end_rotation_delta * Math.random() - 0.5 * end_rotation_delta,
				duration:single_animation_duration,
				easing:Konva.Easings.BounceEaseOut,
				onFinish:() => {
					if(cb != undefined && (col_item.attrs?.is_last_item ?? false)) {
						const wait_until_cb = sound_durations.current[`ms_${milestone_id}`] * 1000 - col_item.attrs?.item_count * interval_between_cell_animations;

						setTimeout(() => {
							cb();
						}, Math.max(500, wait_until_cb));
					}
				},
			});
		}, false, interval_between_cell_animations);
	};

	const failGame = (num_falling_items) => {
		game_ended.current = true;

		if(props.forceStop != undefined) props.forceStop();

		const level_milestone = getMilestoneAtLevel(level.current - 2);

		setTimeout(() => {
			level_milestone.milestoneSound();

			animateFail(level_milestone.id, () => {
				if(is_builder && !is_test) {
					resetGame();
				} else {
					props.onEnded(false);
				}
			});
		}, Math.round(num_falling_items * 30));
	};

	const successGame = (num_falling_items) => {
		game_ended.current = true;
		if(props.forceStop != undefined) props.forceStop();

		setTimeout(() => {
			soundSuccessGame();

			animateSuccess(() => {
				if(is_builder && !is_test) {
					resetGame();
				} else {
					props.onEnded(true);
				}
			});
		}, Math.round(num_falling_items * 30));
	};

	const stopLevel = () => {
		if(game_ended.current === true) return;
		if(animations.current[level.current] == undefined) return;

		stackRow();

		animations.current[level.current].pause();

		const actual_level = getTopLevel();

		let num_falling_items = 0;

		const current_children = refs.current[actual_level].getChildren();

		const tower_top_level = getTopLevel(level.current - 1);
		const num_blocks_in_level = getNumBlocksInLevel(tower_top_level);

		const current_width = current_children.length * item_size.current;
		const tower_top_item = refs.current[tower_top_level];

		const sliding_x = refs.current[actual_level].x();
		const top_x_min = tower_top_item.attrs.avail_x_ini - item_size.current / 2;
		const top_x_max = tower_top_item.attrs.avail_x_end + item_size.current / 2;

		if(sliding_x <= top_x_min) {
			const num_cells_to_fall_left = Math.ceil((top_x_min - sliding_x) / item_size.current);
	
			for(let col = 0; col < num_cells_to_fall_left; col++) {
				setTimeout(() => {
					fallAnimation(level.current - 1, col, 1);
				}, col * 1.1 * 100);
			}

			refs.current[actual_level].attrs.avail_x_ini = refs.current[actual_level].x() + num_cells_to_fall_left * item_size.current;
			num_falling_items = num_cells_to_fall_left;
		} else if((sliding_x + current_width) >= top_x_max) {
			const total_items_in_actual = getNumBlocksInLevel(getTopLevel(level.current));

			const num_cells_to_fall_right = Math.floor((sliding_x + current_width - top_x_max) / item_size.current);

			let index = 0;
			for(let col = total_items_in_actual - 1; col > total_items_in_actual - num_cells_to_fall_right - 1; col--) {
				setTimeout(() => {
					fallAnimation(level.current - 1, col, 1);
				}, index * 1.1 * 100);
				index++;
			}

			refs.current[actual_level].attrs.avail_x_ini = refs.current[actual_level].x();
			num_falling_items = num_cells_to_fall_right;
		}

		refs.current[actual_level].attrs.avail_x_end = refs.current[actual_level].attrs.avail_x_ini + (refs.current[actual_level].attrs.num_blocks - num_falling_items) * item_size.current;
		refs.current[actual_level].attrs.num_blocks = num_blocks_in_level - num_falling_items;

		level.current = level.current + 1;

		if(refs.current[actual_level].attrs.num_blocks < 1) {
			failGame(num_falling_items);
			return;
		}

		updateScore(points_per_level);

		if(level.current == max_num_rows) {
			successGame(num_falling_items);
			return;
		}

		generateRow(level.current, num_blocks_in_level - num_falling_items, -1 * refs.current[actual_level].attrs.direction);
	};

	const resetGame = () => {
		if(refs.current['layer'] == undefined) return;
		if(refs.current['levels'] == undefined) return;

		level.current = 0;
		game_score.current = 0;
		if(props.onScore != undefined) props.onScore(0);

		[...Array(refs.current['levels'].getChildren().length).keys()].map((item, l) => {
			const _level = getTopLevel(l);
			if(refs.current[_level] != undefined) refs.current[_level].destroy();
		});

		killAllAnimations();
		clearAllCaches();

		startAnimations();

		refs.current['layer'].draw();

		setReset({ ...reset });
	};

	const killAllAnimations = () => {
		for(let l = 1; l < level.current + 1; l++) {
			if(animations.current[l] != undefined) {
				animations.current[l].pause();
				animations.current[l] = null;
			}
		}
	};

	const clearCache = (col, row, target = 'item') => {
		if(!refs.default_cache_props[`${target}_${col}_${row}`]) return;
		refs.current[`${target}_${col}_${row}`].clearCache();
	};

	const clearAllCaches = () => {
		/*[...Array(board_grid_col).keys()].map((item_x, col) => {
			[...Array(board_grid_row).keys()].map((item_y, row) => {
				clearCache(col, row, 'item');
				clearCache(col, row, 'item_bg');
				clearCache(col, row, 'item_fg');
			});
		});*/
	};

	const applyCache = (col, row, target = 'item') => {
		refs.current[`${target}_${col}_${row}`].cache(default_cache_props);
	};

	const calculateInitialLevelX = () => {
		const padding = Math.floor(max_num_rows / 4);
		const ini_cell_min_left = padding;	// minimum distance to left board border (in cell units)
		const ini_cell_min_right = padding;	// minimum distance to right board border (in cell units)

		if(!is_playable) return Math.round((max_num_rows - initial_num_blocks) / 2);

		return ini_cell_min_left + Math.round((max_num_rows - initial_num_blocks - ini_cell_min_left - ini_cell_min_right) * Math.random());
	};

	const getCornerRadius = () => {
		return parseFloat(0.5 * item_size.current * corner_radius / 100);
	};

	const generateRow = (level, num_blocks_in_level, direction = 1) => {
		const blocks_in_level = (num_blocks_in_level == undefined) ? initial_num_blocks : num_blocks_in_level;
		if(blocks_in_level < 1) return;

		let color = 'black';
		const actual_milestone = getMilestoneAtLevel(level);
		if(actual_milestone.color != undefined) color = actual_milestone.color;

		let block_x = (level > 0) ? (-(blocks_in_level + 1) * item_size.current) : (calculateInitialLevelX() * item_size.current);
		if(direction < 0) block_x = size.current;

		if(!is_playable && level > 0) {
			const previous_level = getTopLevel(level - 1);

			const skew = Math.random() > 0.5 ? -1 : 1;
			const actual_width = num_blocks_in_level * item_size.current;
			const prev_width = refs.current[previous_level].attrs.num_blocks * item_size.current;
			block_x = refs.current[previous_level].x() + (prev_width - actual_width) / 2 + skew * item_size.current / 4;
		}

		const new_row_group = new Konva.Group({
			y:size.current - (level + 1) * item_size.current,
			x:block_x,
			avail_x_ini:block_x,
			avail_x_end:(level == 0) ? (block_x + blocks_in_level * item_size.current) : null,
			level,
			direction,
			num_blocks:blocks_in_level,
			milestone_id:getMilestoneStringify(actual_milestone),
		});

		const top_level = getTopLevel(level);
		refs.current[top_level] = new_row_group;

		const ghost_item = new Konva.Rect({
			width:blocks_in_level * item_size.current,
			height:item_size.current,
			fill:color,
			cornerRadius:getCornerRadius(),
			opacity:0.3,
			is_ghost:true,
		});
		new_row_group.add(ghost_item);

		[...Array(blocks_in_level).keys()].map((item_x, col) => {
			let item_img = {
				fill:color,
			};

			if(isNotEmpty(actual_milestone, 'image')) {
				const img = (indexed_items?.milestone ?? []).find((m) => (m.name == actual_milestone.image));
				
				if(img) {
					item_img = {
						fillPatternImage:img.fillPatternImage,
						fillPatternRepeat:'no-repeat',
						fillPatternScaleX:img.scale,
						fillPatternScaleY:img.scale,
					};
				}
			}

			const item = new Konva.Rect({
				...item_img,
				x:col * item_size.current + item_size.current / 2,
				y:item_size.current / 2,
				width:item_size.current + ((corner_radius < 1 && col < blocks_in_level - 1) ? 1 : 0),
				height:item_size.current,
				level,
				offsetX:item_size.current / 2,
				offsetY:item_size.current / 2,
				cornerRadius:getCornerRadius(),
				strokeWidth:1,
			});

			refs.current[`${level}_${col}`] = item;
			new_row_group.add(item);
		});

		if(refs.current['levels'] == undefined || refs.current['layer'] == undefined) return;

		refs.current['levels'].add(new_row_group);
		refs.current['layer'].draw();

		if(level > 0 && is_playable) {
			slideTo(direction);
		}
	};

	if(!full_loaded) {
		return (
			<div class="canvas_cont">
				<Spinner>Loading game</Spinner>
			</div>
		)
	}

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			ref={(node) => { refs.current[`main_stage`] = node; }}
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer ref={(node) => { refs.current['layer'] = node; }} >
				<Rect
					ref={(node) => { refs.current[`game_bg`] = node; }}
					width={size.current}
					height={size.current}
					fill={stage?.bg_color ?? '#CCCCCC'}
					fillPatternImage={indexed_items?.full_bg?.fillPatternImage ?? null}
					fillPatternRepeat='no-repeat'
					fillPatternScaleX={indexed_items?.full_bg?.scale ?? null}
					fillPatternScaleY={indexed_items?.full_bg?.scale ?? null}
					fillPriority={(isNotEmpty((indexed_items?.full_bg ?? {}), 'fillPatternImage')) ? 'pattern' : 'color'}
				/>

				<Group ref={node => refs.current['levels'] = node} />

				{(display_grid) && (
					<Group>
						{[...Array(max_num_rows).keys()].map((item_x, col) => {
							return (
								<Line
									key={`grid_col_${col}`}
									points={[0, 0, size.current, 0]}
									stroke={milestones_color}
									opacity={0.3}
									y={item_size.current * col}
									strokeWidth={1}
								/>
							)
						})}

						{[...Array(max_num_rows).keys()].map((item_y, row) => {
							return (
								<Line
									key={`grid_row_${row}`}
									points={[0, 0, 0, size.current - item_size.current]}
									stroke={milestones_color}
									opacity={0.3}
									x={item_size.current * row}
									strokeWidth={1}
								/>
							)
						})}
					</Group>
				)}

				{display_milestone_lines && milestones.length > 1 && milestones.map((ms, m) => {
					if(m == 0) return;

					return (
						<Group
							key={`milestone_${m}`}
							y={size.current - (ms.min_level + 1) * item_size.current}
						>
							<Line
								points={[0, 0, size.current, 0]}
								stroke={milestones_color}
								strokeWidth={1}
							/>

							<Text
								text={ms.title}
								width={size.current}
								height={20}
								align='right'
								fontSize={20}
								fill={milestones_color}
								x={-10}
								y={-28}
							/>
						</Group>
					)
				})}

				<Rect
					width={size.current}
					height={size.current}
					onMouseDown={stopLevel}		// onClick
					onTouchStart={stopLevel}	// onTap
				/>
			</Layer>
		</Stage>
	)
});

export default StackitCanvas;